import { Button, Dialog, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { FormProvider, Scrollbar } from '@app/components';
import { PersonalInfoForm } from './personal-info-form';
import { IAddEmployeeSchema, IEmployee } from '../../types';
import { useDialog, useRequestState } from '@app/hooks';
import { ConfirmDialog } from '@app/components/confirm-dialog';
import { addEmployeeSchema, addInternSchema } from '../../validators';
import { addEmployee, getEmployees, updateEmployee } from '../../slice';
import { selectAddEmployeeState, selectUpdateEmployeeState } from '../../selectors';
import { OfficialInfoForm } from '@app/sections/shared/official-info-form';
import { IAddCandidateSchema } from '@app/modules/candidates/types';
import { IIntern } from '@app/modules/intern/types';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { getRole } from '@app/modules/role';

// ------------------------------------------------
interface props {
  open: boolean;
  title?: string;
  onClose: VoidFunction;
  row?: IEmployee;
  roleDisabled?: boolean;
  data?: IAddCandidateSchema | IIntern;
  showInternRole?: boolean;
}

const AddEmployeePage = ({
  open,
  onClose,
  row,
  data,
  title,
  roleDisabled,
  showInternRole,
}: props) => {
  function getSchema(title?: string) {
    if (title === 'intern') {
      return addEmployeeSchema.concat(addInternSchema);
    } else {
      return addEmployeeSchema;
    }
  }

  const parts = row ? row?.resume?.split('?') : data?.resume?.split('?');
  const filePickerName = parts ? parts[0].split('/').pop() : '';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const methods = useForm<IAddEmployeeSchema>({
    resolver: yupResolver(getSchema(title)) as any,
    defaultValues: {
      resume: row ? get(row, 'resume', '') : get(data, 'resume', ''),
      source: row ? get(row, 'source', '') : get(data, 'source', ''),
      referenceBy: row ? get(row, 'referenceBy', '') : get(data, 'referenceBy', ''),
      other: row ? get(row, 'other', '') : get(data, 'other', ''),
      personalInfo: {
        firstName: row ? get(row, 'firstName', '') : get(data, 'firstName', ''),
        lastName: row ? get(row, 'lastName', '') : get(data, 'lastName', ''),
        personalEmail: row ? get(row, 'personalEmail', '') : get(data, 'email', ''),
        phone: row ? get(row, 'phone', '') : get(data, 'phone', ''),
        presentAddress: row?.presentAddress ? row?.presentAddress : {},
        permanentAddress: row?.permanentAddress ? row?.permanentAddress : {},
        dob: row ? get(row, 'dob', null) : null,
        aboutMe: get(row, 'aboutMe', ''),
        maritalStatus: row?.maritalStatus === 'undefined' ? '' : row?.maritalStatus,
        gender: row?.gender === 'undefined' ? '' : row?.gender,
        employeeImage: get(row, 'profileImage'),
      },
      officialInfo: {
        officialEmail: get(row, 'officialEmail', ''),
        roleId: (title === 'intern' ? 3 : get(row, 'roleId')) || get(data, 'roleId', ''),
        managerId: row?.managerId ? row.managerId.map((id) => Number(id)) : [],
        joiningDate: get(row, 'joiningDate', ''),
        department: row?.department,
        designation: row?.designation,
      },
      documentInfo: {
        aadharFront: get(row, 'aadharFront', ''),
        aadharBack: get(row, 'aadharBack', ''),
        panFront: get(row, 'panFront', ''),
        panBack: get(row, 'panBack', ''),
      },
      bankInfo: {
        bankInformation: {
          accountHolderName: get(row, 'bankInformation.accountHolderName', ''),
          fatherName: get(row, 'bankInformation.fatherName', ''),
          bankName: get(row, 'bankInformation.bankName', ''),
          ifscCode: get(row, 'bankInformation.ifscCode', ''),
          accountType: get(row, 'bankInformation.accountType', ''),
          accountNumber: get(row, 'bankInformation.accountNumber'),
          reenterAccountNumber: get(row, 'bankInformation.reenterAccountNumber'),
        },
      },
      emergencyContactInfo: {
        emergencyContact: row?.emergencyContact ? [...row?.emergencyContact] : [],
      },
      educationInfo: {
        education: row?.education ? [...row?.education] : [],
      },
      workExperienceInfo: {
        workExperience: row?.workExperience ? [...row?.workExperience] : [],
      },
      samePresentAddress: false,
    },
  });

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(getRole());
  }, [dispatch]);

  const onCloseHandler = useCallback(() => {
    if (roleDisabled) {
      if (data?.roleId === 3) {
        navigate(PATH_DASHBOARD.internList);
      } else if (data?.roleId === 5) {
        navigate(PATH_DASHBOARD.employeeList);
      }
    } else {
      onClose();
    }
  }, [roleDisabled, data?.roleId]);

  const { loading } = useRequestState({
    stateSelector: data ?? row ? selectUpdateEmployeeState : selectAddEmployeeState,
    onSuccess: onCloseHandler,
    successMessageShown: true,
    errorShown: true,
  });

  const onSave = (form: IAddEmployeeSchema) => {
    confirmingDialog.show();
    console.log(form);
  };
  const onSubmit = (form: IAddEmployeeSchema) => {
    row
      ? dispatch(updateEmployee({ ...form, id: row?.id, resume: methods.getValues('resume') }))
      : data
      ? dispatch(updateEmployee({ ...form, id: data?.id, resume: methods.getValues('resume') }))
      : dispatch(addEmployee({ ...form, resume: methods.getValues('resume') }));
  };
  const setOfficialAddress = useCallback(
    (officialEmailValue: string) => {
      const lowercaseEmail = officialEmailValue.toLowerCase();
      const fieldState = methods.getFieldState('officialInfo.officialEmail');

      if (!fieldState.isTouched) {
        methods.setValue('officialInfo.officialEmail', lowercaseEmail);
      }
    },
    [methods]
  );

  const confirmingDialog = useDialog();
  const officialEmail = methods.getValues('officialInfo.officialEmail');
  const personalEmail = methods.getValues('personalInfo.personalEmail');

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <Header
          title={
            title === 'intern'
              ? Strings.addIntern.manageIntern
              : row
              ? Strings.addEmployee.updateEmployee
              : Strings.addEmployee.manageEmployee
          }
          variant="dialog"
          onClose={onClose}
        />
        <Scrollbar>
          <DialogContent>
            <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSave)}>
              <PersonalInfoForm
                methods={methods}
                data={row}
                title={title}
                filePickerName={filePickerName}
              />
              <OfficialInfoForm
                setOfficialAddress={setOfficialAddress}
                joiningDate={row?.joiningDate}
                title={title}
                roleDisabled={roleDisabled}
                hideCandidateAndIntern={!!(!row && !data && !showInternRole)}
              />
            </FormProvider>
          </DialogContent>
        </Scrollbar>
        <Divider />
        <DialogActions>
          <Button variant="text" color="inherit" onClick={onClose}>
            {Strings.button.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={methods.handleSubmit(onSave)}
          >
            {title === 'intern' ? Strings.addIntern.saveIntern : Strings.button.saveEmployee}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        title={
          title === 'intern' ? `${Strings.addIntern.saveIntern}?` : `${Strings.field.addMember}?`
        }
        content={
          <>
            {Strings.common.confirmCaption}
            <Typography variant="h6">
              {officialEmail}, {personalEmail}
            </Typography>
          </>
        }
        open={confirmingDialog.visible}
        onClose={confirmingDialog.hide}
        action={
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {Strings.button.confirm}
          </LoadingButton>
        }
      />
    </>
  );
};

export { AddEmployeePage };
